import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { useAuth } from '../Context/AuthContext';
import { addressService } from '../services/addressService';
import api from '../services/api';
import './Checkout.css';

const loadRazorpay = () => {
    return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

const Checkout = ({ clearCart }) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { user } = useAuth();

    const {
        cartItems = [],
        subtotal = 0,
        from = '/gallery',  // origin page — passed through so Cart's Back still works
    } = state || {};

    // The address saved in the backend (null = none found)
    const [savedAddress, setSavedAddress] = useState(null);

    // 'saved' | 'new'
    const [addressMode, setAddressMode] = useState('saved');

    // Payment success state
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);

    // Tracks when the user clicks 'Complete Payment' to hide the form via CSS
    const [paymentInitiated, setPaymentInitiated] = useState(false);

    // Form state — pre-filled from user + savedAddress once loaded
    const [form, setForm] = useState({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        country: '',
    });

    // Dynamic shipping logic based on Country text
    const countryStr = form.country.trim().toLowerCase();

    let surchargeDisplay = "Pending calculation";
    let surcharge = 0;

    if (countryStr === "") {
        surcharge = 0;
        surchargeDisplay = "Pending calculation";
    } else if (countryStr === "india") {
        surcharge = 100;
        surchargeDisplay = "Rs 100.00";
    } else {
        surcharge = 4000;
        surchargeDisplay = "Rs 4000.00";
    }

    const finalTotal = subtotal + surcharge;

    // ── On mount: pre-fill name/email/phone from auth, fetch saved address ──
    useEffect(() => {
        if (!user) return;

        // Pre-fill personal fields immediately from logged-in user
        setForm((prev) => ({
            ...prev,
            fullName: user.fullName || user.name || '',
            email: user.email || '',
            phone: user.phone || user.phoneNumber || '',
        }));

        // Fetch saved address from backend
        const fetchAddress = async () => {
            const result = await addressService.getAddresses(user.id);
            if (result?.success && result.data?.length > 0) {
                // Use the default address (backend returns default first)
                const addr = result.data[0];
                setSavedAddress(addr);
                // Pre-fill address fields with saved address
                setForm((prev) => ({
                    ...prev,
                    address: addr.street || '',
                    city: addr.city || '',
                    state: addr.state || '',
                    pincode: addr.zip_code || '',
                    country: addr.country || '',
                }));
            }
        };

        fetchAddress();
    }, [user]);

    // ── When the user switches address mode ──
    const handleAddressModeChange = (mode) => {
        setAddressMode(mode);

        if (mode === 'saved' && savedAddress) {
            // Restore saved address fields
            setForm((prev) => ({
                ...prev,
                address: savedAddress.street || '',
                city: savedAddress.city || '',
                state: savedAddress.state || '',
                pincode: savedAddress.zip_code || '',
                country: savedAddress.country || '',
            }));
        } else {
            // Clear address fields for manual entry
            setForm((prev) => ({
                ...prev,
                address: '',
                city: '',
                state: '',
                pincode: '',
                country: '',
            }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // Address fields are read-only when user has chosen to use their saved address
    const addressReadOnly = savedAddress !== null && addressMode === 'saved';

    // Razorpay Integration
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Hide the form visually immediately to prevent flash
        setPaymentInitiated(true);

        const isLoaded = await loadRazorpay();
        if (!isLoaded) {
            setPaymentError('Razorpay SDK failed to load. Please check your internet connection.');
            setPaymentInitiated(false);
            return;
        }

        try {
            // 1. Create order on the backend securely
            const response = await api.post('/payment/create-order', {
                userId: user.id,
                surcharge: surcharge
            });

            if (!response.data.success) {
                setPaymentError("Failed to initiate payment: " + response.data.message);
                setPaymentInitiated(false);
                return;
            }

            const order = response.data.order;

            // 2. Open Razorpay Checkout modal
            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Controlled via frontend .env
                amount: order.amount,
                currency: order.currency,
                name: "Ithal by MR",
                description: "Order Checkout",
                order_id: order.id,
                prefill: {
                    name: form.fullName,
                    email: form.email,
                    contact: form.phone,
                },
                theme: {
                    color: "#000000"
                },
                handler: async function (res) {
                    setIsVerifyingPayment(true);
                    try {
                        const verifyRes = await api.post('/payment/verify-payment', {
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                            userId: user.id,
                            total: finalTotal,
                            surcharge: surcharge,
                            addressDetails: {
                                name: form.fullName,
                                email: form.email,
                                phone: form.phone,
                                address: form.address,
                                city: form.city,
                                state: form.state,
                                pincode: form.pincode,
                                country: form.country
                            }
                        });

                        if (verifyRes.data.success) {
                            // Clear cart on successful order
                            if (clearCart) {
                                clearCart();
                            }
                            setPaymentSuccess(true);
                        } else {
                            setPaymentError('Payment verification failed.');
                            setPaymentInitiated(false);
                        }
                    } catch (error) {
                        console.error('Verification Error:', error);
                        setPaymentError('Something went wrong verifying your payment.');
                        setPaymentInitiated(false);
                    } finally {
                        setIsVerifyingPayment(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);

            rzp.on('payment.failed', function (paymentResponse) {
                setPaymentError(`Payment failed: ${paymentResponse.error.description}`);
                setPaymentInitiated(false);
                setIsVerifyingPayment(false);
            });

            rzp.open();

        } catch (error) {
            console.error('Payment Error:', error);
            setPaymentError('Something went wrong while initiating the payment.');
            setPaymentInitiated(false);
        }
    };

    // ── Verifying payment screen ──
    if (isVerifyingPayment) {
        return (
            <div className="checkout-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: '40px 20px', textAlign: 'center', backgroundColor: '#fff',
                    borderRadius: '12px', maxWidth: '500px', width: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                    <div style={{
                        width: '40px', height: '40px', margin: '0 auto 16px auto',
                        border: '4px solid #f3f3f3', borderTop: '4px solid #10b981',
                        borderRadius: '50%', animation: 'spin 1s linear infinite'
                    }}></div>
                    <h2 style={{ fontSize: '20px', color: '#111827', marginBottom: '8px', fontWeight: '500' }}>
                        Verifying your payment...
                    </h2>
                    <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Please do not close or refresh this page.</p>
                </div>
            </div>
        );
    }

    // ── Payment success screen ──
    if (paymentSuccess) {
        return (
            <div className="checkout-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="checkout-success-container" style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: '40px 20px', textAlign: 'center', backgroundColor: '#fff',
                    borderRadius: '12px', maxWidth: '500px', width: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                    <div style={{
                        width: '72px', height: '72px', backgroundColor: '#10b981',
                        borderRadius: '50%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', marginBottom: '24px'
                    }}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '24px', color: '#111827', marginBottom: '12px', fontWeight: '600' }}>Order Placed Successfully!</h2>
                    <p style={{ fontSize: '15px', color: '#4b5563', marginBottom: '32px', lineHeight: '1.5' }}>
                        Thank you for your purchase. We have received your order and will process it shortly.
                    </p>
                    <button
                        onClick={() => navigate('/', { replace: true })}
                        className="btn-complete-payment"
                        style={{ width: '100%', maxWidth: '250px', justifyContent: 'center' }}
                    >
                        Continue Shopping
                    </button>
                </div>
            </div>
        );
    }

    // ── Payment error screen ──
    if (paymentError) {
        return (
            <div className="checkout-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="checkout-error-container" style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: '40px 20px', textAlign: 'center', backgroundColor: '#fff',
                    borderRadius: '12px', maxWidth: '500px', width: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                    <div style={{
                        width: '72px', height: '72px', backgroundColor: '#ef4444',
                        borderRadius: '50%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', marginBottom: '24px'
                    }}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '24px', color: '#111827', marginBottom: '12px', fontWeight: '600' }}>Payment Failed</h2>
                    <p style={{ fontSize: '15px', color: '#4b5563', marginBottom: '32px', lineHeight: '1.5' }}>
                        {paymentError}
                    </p>
                    <button
                        onClick={() => { setPaymentError(''); setPaymentInitiated(false); }}
                        className="btn-complete-payment"
                        style={{ width: '100%', maxWidth: '250px', justifyContent: 'center' }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout-page">

            {/* ── Back Navigation ── */}
            <button className="checkout-back-nav" onClick={() => navigate(-1)}>
                <ArrowLeft size={18} />
                <span>Back to Cart</span>
            </button>

            <div 
                className="checkout-layout" 
                style={{ 
                    opacity: paymentInitiated && !isVerifyingPayment && !paymentError ? 0 : 1, 
                    pointerEvents: paymentInitiated && !isVerifyingPayment && !paymentError ? 'none' : 'auto',
                    transition: 'opacity 0.1s ease-in-out'
                }}
            >

                {/* ═══════════════════════════════════════
                    LEFT — Checkout Details Form
                ═══════════════════════════════════════ */}
                <div className="checkout-form-section">
                    <h2 className="checkout-title">Checkout Details</h2>

                    <form className="checkout-form" onSubmit={handleSubmit}>

                        {/* Personal details — always pre-filled, always editable */}
                        <div className="form-group">
                            <label htmlFor="fullName">Full Name <span className="required">*</span></label>
                            <input
                                id="fullName"
                                name="fullName"
                                type="text"
                                value={form.fullName}
                                onChange={handleChange}
                                placeholder="Enter your full name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email <span className="required">*</span></label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                placeholder="Enter your email"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">Phone Number <span className="required">*</span></label>
                            <input
                                id="phone"
                                name="phone"
                                type="tel"
                                value={form.phone}
                                onChange={handleChange}
                                placeholder="Enter your phone number"
                                required
                            />
                        </div>

                        {/* ── Delivery Address Section ── */}
                        <div className="address-section">
                            <p className="address-section-label">
                                Delivery Address <span className="required">*</span>
                            </p>

                            {/* Address mode toggle — only shown when a saved address exists */}
                            {savedAddress && (
                                <div className="address-mode-toggle">
                                    <label className={`address-mode-option ${addressMode === 'saved' ? 'active' : ''}`}>
                                        <input
                                            type="radio"
                                            name="addressMode"
                                            value="saved"
                                            checked={addressMode === 'saved'}
                                            onChange={() => handleAddressModeChange('saved')}
                                        />
                                        Use my saved address
                                    </label>
                                    <label className={`address-mode-option ${addressMode === 'new' ? 'active' : ''}`}>
                                        <input
                                            type="radio"
                                            name="addressMode"
                                            value="new"
                                            checked={addressMode === 'new'}
                                            onChange={() => handleAddressModeChange('new')}
                                        />
                                        Use a different address
                                    </label>
                                </div>
                            )}

                            <div className="form-group">
                                <textarea
                                    id="address"
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    placeholder="Enter your delivery address"
                                    rows={3}
                                    required
                                    readOnly={addressReadOnly}
                                    className={addressReadOnly ? 'field-readonly' : ''}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <input
                                        id="city"
                                        name="city"
                                        type="text"
                                        value={form.city}
                                        onChange={handleChange}
                                        placeholder="City"
                                        required
                                        readOnly={addressReadOnly}
                                        className={addressReadOnly ? 'field-readonly' : ''}
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        id="pincode"
                                        name="pincode"
                                        type="text"
                                        value={form.pincode}
                                        onChange={handleChange}
                                        placeholder="Zip Code"
                                        required
                                        readOnly={addressReadOnly}
                                        className={addressReadOnly ? 'field-readonly' : ''}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <input
                                        id="state"
                                        name="state"
                                        type="text"
                                        value={form.state}
                                        onChange={handleChange}
                                        placeholder="State"
                                        required
                                        readOnly={addressReadOnly}
                                        className={addressReadOnly ? 'field-readonly' : ''}
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        id="country"
                                        name="country"
                                        type="text"
                                        value={form.country}
                                        onChange={handleChange}
                                        placeholder="Country"
                                        required
                                        readOnly={addressReadOnly}
                                        className={addressReadOnly ? 'field-readonly' : ''}
                                    />
                                </div>
                            </div>
                        </div>

                        <button type="submit" className="btn-complete-payment">
                            <CreditCard size={18} />
                            Complete Payment
                        </button>

                    </form>
                </div>

                {/* ═══════════════════════════════════════
                    RIGHT — Order Summary
                ═══════════════════════════════════════ */}
                <div className="checkout-summary">

<h2 className="summary-title">
  Order Summary ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items)
</h2>


                    {/* Item list */}
                    <div className="summary-items">
                        {cartItems.map((item) => {
                            const itemPrice = parseFloat(item.price.replace(/[^0-9.-]+/g, ''));
                            const itemTotal = itemPrice * item.quantity;
                            return (
                                <div key={item.id} className="summary-item">
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="summary-item-image"
                                    />
                                    <div className="summary-item-info">
                                        <p className="summary-item-name">{item.title}</p>
                                        <p className="summary-item-qty">Qty: {item.quantity}</p>
                                        <p className="summary-item-price">Rs {itemTotal.toFixed(2)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="summary-divider" />

                    {/* Subtotal */}
                    <div className="summary-row">
                        <span>Subtotal</span>
                        <span>Rs {subtotal.toFixed(2)}</span>
                    </div>

                    {/* Shipping surcharge */}
                    <div className="summary-row">
                        <span>Shipping ({countryStr ? form.country : "Pending"})</span>
                        <span className="surcharge-value">{surchargeDisplay}</span>
                    </div>

                    <div className="summary-divider" />

                    {/* Total */}
                    <div className="summary-total-row">
                        <span>Total</span>
                        <span className="total-value">Rs {finalTotal.toFixed(2)}</span>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Checkout;
